import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, FileText, Users, Clock, FolderOpen, User, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ProjectService } from '../../services/projectService';
import { OperationsSection } from './OperationsSection';
import { ProcessingIndicator } from '../indicators/ProcessingIndicator';
import { fadeInUp, fadeInDown, staggerContainer } from '../animations/AnimatedOperations';
import { useWebSocket } from '../../contexts/WebSocketContext';

export function Sidebar({
  activeSection,
  setActiveSection,
  selectedProject,
  setSelectedProject,
  onProjectSelect,
  onBackToProjects,
  onCollectionSelect,
  userInfo
}) {
  const { joinProject, leaveProject, subscribe } = useWebSocket();
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [projects, setProjects] = useState([]);
  const [projectsWithCollections, setProjectsWithCollections] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [joinedProjects, setJoinedProjects] = useState(new Set());

  // Fetch projects when component mounts or userInfo changes
  useEffect(() => {
    if (userInfo?.userId) {
      fetchProjects();
    }
  }, [userInfo?.userId]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      // Cleanup: leave all joined project rooms when component unmounts
      joinedProjects.forEach(projectId => {
        leaveProject(projectId);
        console.log(`🔌 Sidebar: Left project room on unmount: ${projectId}`);
      });
    };
  }, []);

  const fetchProjects = async () => {
    if (!userInfo?.userId) return;

    try {
      setLoading(true);
      const projectsData = await ProjectService.getUserProjects(userInfo.userId);
      setProjects(projectsData);

      // Join WebSocket rooms for all projects to receive updates
      projectsData.forEach(project => {
        if (!joinedProjects.has(project._id)) {
          joinProject(project._id);
          setJoinedProjects(prev => new Set(prev).add(project._id));
          console.log(`🔌 Sidebar: Joined project room: ${project._id}`);
        }
      });

    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectCollections = async (projectId) => {
    if (projectsWithCollections.has(projectId)) {
      return; // Already loaded
    }

    try {
      const collections = await ProjectService.getProjectCollections(projectId, userInfo.userId);
      setProjectsWithCollections(prev => new Map(prev.set(projectId, collections)));
    } catch (error) {
      console.error('Error fetching project collections:', error);
      // Set empty array to prevent repeated attempts
      setProjectsWithCollections(prev => new Map(prev.set(projectId, [])));
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setActiveSection(project.name);
    if (onProjectSelect) {
      onProjectSelect(project);
    }
  };

  const toggleProjectExpansion = async (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      // Fetch collections when expanding
      await fetchProjectCollections(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleCollectionClick = (project, collection) => {
    // First select the project
    setSelectedProject(project);
    setActiveSection(project.name);
    // Then navigate to the collection
    if (onCollectionSelect) {
      onCollectionSelect(collection);
    }
  };

  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    if (!userInfo?.userId) return;

    // Subscribe to project-level events
    const unsubscribeProjectCreated = subscribe('project:created', (data) => {
      console.log('📥 Sidebar received project:created:', data);

      // Add new project to the list
      setProjects(prev => {
        const exists = prev.some(p => p._id === data.project._id);
        if (exists) {
          console.log('Project already exists locally, skipping duplicate');
          return prev;
        }
        return [...prev, data.project];
      });

      // Join the WebSocket room for the new project
      if (!joinedProjects.has(data.project._id)) {
        joinProject(data.project._id);
        setJoinedProjects(prev => new Set(prev).add(data.project._id));
        console.log(`🔌 Sidebar: Joined new project room: ${data.project._id}`);
      }
    });

    const unsubscribeProjectUpdated = subscribe('project:updated', (data) => {
      console.log('📥 Sidebar received project:updated:', data);

      // Update the project in the list
      setProjects(prev => prev.map(p =>
        p._id === data.project._id ? data.project : p
      ));
    });

    const unsubscribeProjectDeleted = subscribe('project:deleted', (data) => {
      console.log('📥 Sidebar received project:deleted:', data);

      // Remove the project from the list
      setProjects(prev => prev.filter(p => p._id !== data.projectId));

      // Leave the WebSocket room
      if (joinedProjects.has(data.projectId)) {
        leaveProject(data.projectId);
        setJoinedProjects(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.projectId);
          return newSet;
        });
        console.log(`🔌 Sidebar: Left deleted project room: ${data.projectId}`);
      }

      // Clean up collections for this project
      setProjectsWithCollections(prev => {
        const newMap = new Map(prev);
        newMap.delete(data.projectId);
        return newMap;
      });
    });

    // Subscribe to collection events
    const unsubscribeCollectionCreated = subscribe('collection:created', (data) => {
      console.log('📥 Sidebar received collection:created:', data);

      // Update the collections for the specific project
      setProjectsWithCollections(prev => {
        const newMap = new Map(prev);
        const projectCollections = newMap.get(data.projectId) || [];

        // Check if collection already exists to avoid duplicates
        const exists = projectCollections.some(c => c._id === data.collection._id);
        if (!exists) {
          newMap.set(data.projectId, [...projectCollections, data.collection]);
        }
        return newMap;
      });

      // Update project collection count
      setProjects(prev => prev.map(p =>
        p._id === data.projectId
          ? { ...p, collectionsCount: (p.collectionsCount || 0) + 1 }
          : p
      ));
    });

    const unsubscribeCollectionUpdated = subscribe('collection:updated', (data) => {
      console.log('📥 Sidebar received collection:updated:', data);

      // Update the specific collection in the map
      setProjectsWithCollections(prev => {
        const newMap = new Map(prev);
        const projectCollections = newMap.get(data.projectId) || [];

        const updatedCollections = projectCollections.map(c =>
          c._id === data.collection._id ? data.collection : c
        );
        newMap.set(data.projectId, updatedCollections);
        return newMap;
      });
    });

    const unsubscribeCollectionDeleted = subscribe('collection:deleted', (data) => {
      console.log('📥 Sidebar received collection:deleted:', data);

      // Remove the collection from the map
      setProjectsWithCollections(prev => {
        const newMap = new Map(prev);
        const projectCollections = newMap.get(data.projectId) || [];

        const filteredCollections = projectCollections.filter(c => c._id !== data.collectionId);
        newMap.set(data.projectId, filteredCollections);
        return newMap;
      });

      // Update project collection count
      setProjects(prev => prev.map(p =>
        p._id === data.projectId
          ? { ...p, collectionsCount: Math.max(0, (p.collectionsCount || 0) - 1) }
          : p
      ));
    });

    // Subscribe to file events to update collection file counts
    const unsubscribeFileUploaded = subscribe('file:uploaded', (data) => {
      if (data.collectionId) {
        console.log('📥 Sidebar received file:uploaded for collection:', data);

        // Update the file count for the specific collection
        setProjectsWithCollections(prev => {
          const newMap = new Map(prev);
          const projectCollections = newMap.get(data.projectId) || [];

          const updatedCollections = projectCollections.map(c =>
            c._id === data.collectionId
              ? { ...c, fileCount: (c.fileCount || 0) + 1 }
              : c
          );
          newMap.set(data.projectId, updatedCollections);
          return newMap;
        });
      }
    });

    const unsubscribeFileDeleted = subscribe('file:deleted', (data) => {
      if (data.collectionId) {
        console.log('📥 Sidebar received file:deleted for collection:', data);

        // Update the file count for the specific collection
        setProjectsWithCollections(prev => {
          const newMap = new Map(prev);
          const projectCollections = newMap.get(data.projectId) || [];

          const updatedCollections = projectCollections.map(c =>
            c._id === data.collectionId
              ? { ...c, fileCount: Math.max(0, (c.fileCount || 0) - 1) }
              : c
          );
          newMap.set(data.projectId, updatedCollections);
          return newMap;
        });
      }
    });

    // Cleanup subscriptions
    return () => {
      unsubscribeProjectCreated();
      unsubscribeProjectUpdated();
      unsubscribeProjectDeleted();
      unsubscribeCollectionCreated();
      unsubscribeCollectionUpdated();
      unsubscribeCollectionDeleted();
      unsubscribeFileUploaded();
      unsubscribeFileDeleted();
    };
  }, [subscribe, userInfo]);

  return (
    <div className="w-64 bg-[#2a2829] border-r border-[#494646] flex flex-col h-full relative">
      {/* Scrollable content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Teams Section - Disabled */}
        <div className="px-4 py-4 border-b border-[#494646] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-md font-semibold text-gray-400">Teams</span>
            <Lock className="h-3 w-3 text-gray-500 ml-auto" />
          </div>
        </div>

        {/* Projects Section - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-[#494646] scrollbar-track-transparent hover:scrollbar-thumb-[#5a5757]">
          <div className="p-4 pb-8">
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="h-4 w-4 text-gray-400" />
              <span className="text-md font-semibold text-gray-200">Projects</span>
            </div>

            <div className="space-y-1">
              {/* Projects List - Always Visible */}
              {loading ? (
                <motion.div
                  className="flex items-center justify-center py-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0c8ce9]"></div>
                </motion.div>
              ) : projects.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-xs">
                  No projects found
                </div>
              ) : (
                <motion.div
                  initial="initial"
                  animate="animate"
                >
                  {projects.map((project, index) => {
                    const projectCollections = projectsWithCollections.get(project._id) || [];
                    const hasCollections = project.collectionsCount > 0;

                    return (
                      <motion.div
                        key={project._id}
                        variants={fadeInDown}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: index * 0.05,
                          duration: 0.2,
                          ease: "easeOut"
                        }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleProjectClick(project)}
                            className={cn(
                              "w-full flex items-center justify-between text-xs h-7 px-2 transition-all duration-200",
                              selectedProject?._id === project._id
                                ? "bg-[#494646] text-gray-100"
                                : "text-gray-400 hover:text-gray-200 hover:bg-[#494646]"
                            )}
                          >
                            <div className="flex items-center min-w-0 flex-1">
                              <FileText className="h-3 w-3 mr-2 flex-shrink-0" />
                              <span className="truncate text-left">{project.name}</span>
                              <ProcessingIndicator
                                projectId={project._id}
                                size="xs"
                                className="ml-1"
                              />
                            </div>
                            {hasCollections && (
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleProjectExpansion(project._id);
                                }}
                                className="ml-1 flex-shrink-0"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <motion.div
                                  animate={{ rotate: expandedProjects.has(project._id) ? 90 : 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ChevronRight className="h-3 w-3" />
                                </motion.div>
                              </motion.button>
                            )}
                          </Button>
                        </motion.div>

                        {/* Collections under project */}
                        <AnimatePresence>
                          {expandedProjects.has(project._id) && projectCollections.length > 0 && (
                            <motion.div
                              className="ml-5 mt-1 space-y-1 overflow-hidden"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                            >
                              {projectCollections.map((collection, collectionIndex) => (
                                <motion.div
                                  key={collection._id}
                                  initial={{ opacity: 0, y: -5 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    delay: collectionIndex * 0.03,
                                    duration: 0.15,
                                    ease: "easeOut"
                                  }}
                                >
                                  <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCollectionClick(project, collection)}
                                      className="w-full flex items-center justify-start text-xs h-6 px-2 text-gray-500 hover:text-gray-300 hover:bg-[#494646] transition-all duration-200"
                                    >
                                      <FolderOpen className="h-3 w-3 mr-2 flex-shrink-0" />
                                      <span className="truncate flex-1 text-left">{collection.name}</span>
                                      <ProcessingIndicator
                                        projectId={project._id}
                                        collectionId={collection._id}
                                        size="xs"
                                        className="mr-1"
                                      />
                                      <span className="text-xs flex-shrink-0">({collection.fileCount || 0})</span>
                                    </Button>
                                  </motion.div>
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Operations Section - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-[#494646] bg-[#2a2829] relative z-10">
        <OperationsSection />
      </div>
    </div>
  );
}